"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const VideoBannerSection = ({ data }) => {
  const videoUrl =
    data?.url || data?.video?.url || data?.videoUrl || "https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/market.mp4";

  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Handle video playback
  useEffect(() => {
    if (!isClient || !videoRef.current) return;

    const video = videoRef.current;

    const ensureVideoPlays = () => {
      if (video.paused && video.readyState >= 2) {
        video.play().catch(() => {
          // Autoplay prevented, will retry on user interaction
        });
      }
    };

    // Try to play when video can play
    const handleCanPlay = () => {
      ensureVideoPlays();
    };

    // Try to play when video has loaded data
    const handleLoadedData = () => {
      ensureVideoPlays();
    };

    // Try to play when video starts playing (ensure it continues)
    const handlePlay = () => {
      // Video is playing
    };

    // Handle errors
    const handleError = () => {
      // Retry loading on error
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.load();
        }
      }, 1000);
    };

    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("play", handlePlay);
    video.addEventListener("error", handleError);

    // Initial attempt to play
    ensureVideoPlays();

    return () => {
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("error", handleError);
    };
  }, [isClient, videoUrl]);

  const { scrollYProgress } = useScroll({
    target: isClient ? sectionRef : undefined,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.5, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  // Single component structure - video persists across all views
  // Use CSS for responsive behavior instead of separate components
  const SectionWrapper = isClient ? motion.section : "section";
  const VideoWrapper = isClient ? motion.div : "div";

  const sectionProps = isClient
    ? {
        ref: sectionRef,
        className: "relative h-[220px] md:h-screen overflow-hidden",
        style: { y, opacity },
      }
    : {
        ref: sectionRef,
        className: "relative h-[220px] md:h-screen overflow-hidden",
      };

  const videoWrapperProps = isClient
    ? {
        className: "absolute inset-0",
        style: { scale },
      }
    : {
        className: "absolute inset-0",
      };

  return (
    <SectionWrapper {...sectionProps}>
      <VideoWrapper {...videoWrapperProps}>
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
          key={videoUrl}
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      </VideoWrapper>

      {/* Floating Elements - only show on desktop */}
      {isClient && (
        <>
          <motion.div
            className="absolute top-1/4 right-1/4 w-16 h-16 bg-[#dfa428]/30 rounded-full hidden md:block"
            animate={{
              y: [0, -40, 0],
              rotate: [0, 180, 360],
              scale: [1, 1.3, 1],
            }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-1/4 left-1/4 w-12 h-12 bg-white/20 rounded-full hidden md:block"
            animate={{
              y: [0, 40, 0],
              rotate: [360, 180, 0],
              scale: [1.3, 1, 1.3],
            }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </>
      )}
    </SectionWrapper>
  );
};

export default VideoBannerSection;
